<script setup lang="ts">
/**
 * A run's record, as the portal's run page lists it (apps/portal/src/views/RunDetailView.vue): one rule
 * down the left, each event a bordered step hung off it — its type as the API names it, when, and what it
 * means in words. A step that waited on a human takes the action tone (BG §18.4), the step where the run
 * failed the failed tone, and a note the OS made is dashed. The order is the caller's; a step that joins a
 * live record slides in.
 *
 * Data in (lib/surfaces.ts RunStepView), drawing out. The decision on a waiting step comes through the
 * `decide` slot: the portal's real approve and reject, or the site's drawn ones. The `body` slot, scoped by
 * the step, carries what the portal shows of the raw event (its payload).
 */
import type { RunStepView } from '../lib/surfaces'

defineProps<{ steps: readonly RunStepView[]; label?: string }>()
</script>

<template>
    <TransitionGroup tag="ol" name="zh-run-step" class="zh-run-timeline" :aria-label="label ?? 'The run’s record'">
        <li
            v-for="step in steps"
            :key="step.id ?? `${step.type}-${step.at}-${step.title}`"
            class="zh-run-timeline__step"
            :data-tone="step.tone"
            :data-type="step.type"
        >
            <p class="zh-run-timeline__head">
                <span class="zh-run-timeline__type">{{ step.type }}</span><span class="zh-run-timeline__at">{{ step.at }}</span>
            </p>
            <p v-if="step.title" class="zh-run-timeline__title">{{ step.title }}</p>
            <p v-if="step.detail" class="zh-run-timeline__detail">{{ step.detail }}</p>
            <div v-if="$slots.body" class="zh-run-timeline__body"><slot name="body" :step="step" /></div>
            <div v-if="step.decide && $slots.decide" class="zh-run-timeline__decide"><slot name="decide" :step="step" /></div>
        </li>
    </TransitionGroup>
</template>

<style scoped>
p {
    margin: 0;
}

.zh-run-timeline {
    position: relative;
    display: grid;
    gap: 0.625rem;
    margin: 0;
    padding: 0 0 0 1.5rem;
    list-style: none;
    border-left: 1px solid var(--line);
}

.zh-run-timeline__step {
    --tone: var(--state-complete);
    position: relative;
    padding: 0.75rem 1rem;
    border: 1px solid var(--line);
    background: var(--panel-2);
}

.zh-run-timeline__step[data-tone="action"] {
    --tone: var(--state-action);
    border-color: color-mix(in srgb, var(--state-action) 60%, transparent);
    background: color-mix(in srgb, var(--state-action) 5%, var(--panel));
}

.zh-run-timeline__step[data-tone="failed"] {
    --tone: var(--state-failed);
    border-color: color-mix(in srgb, var(--state-failed) 60%, transparent);
    background: color-mix(in srgb, var(--state-failed) 4%, var(--panel));
}

.zh-run-timeline__step[data-tone="note"] {
    --tone: var(--state-inactive);
    border-style: dashed;
    background: none;
}

/* The step's mark on the rule: a small square in its tone. */
.zh-run-timeline__step::before {
    content: "";
    position: absolute;
    top: 1rem;
    left: calc(-1.5rem - 4.5px);
    width: 8px;
    height: 8px;
    border: 1.5px solid var(--tone);
    background: var(--panel);
}

.zh-run-timeline__head {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: var(--tracking-system);
}

.zh-run-timeline__type {
    color: var(--tone);
}

.zh-run-timeline__at {
    color: var(--ink-3);
}

.zh-run-timeline__title {
    margin-top: 0.25rem;
    color: var(--ink-1);
    font-weight: 600;
}

.zh-run-timeline__detail {
    margin-top: 0.125rem;
    color: var(--ink-2);
    font-size: 0.9375rem;
    line-height: 1.4;
}

.zh-run-timeline__body {
    margin-top: 0.5rem;
}

.zh-run-timeline__decide {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.625rem;
}

/* A step joining a live record slides down into place; the rest make room. */
@media (prefers-reduced-motion: no-preference) {
    .zh-run-step-enter-active,
    .zh-run-step-move {
        transition:
            opacity 300ms ease-out,
            translate 300ms ease-out;
    }

    .zh-run-step-enter-from {
        opacity: 0;
        translate: 0 -0.5rem;
    }
}
</style>
